import type { CustomOutput } from "shared"
import {
	CardView,
	CustomDropdown,
	InputLikeGridWithLabel,
	SignUpInputField,
	SignUpSelectField,
	adminAddClient,
	bytesToMegabytes,
	DEFAULT_AVAILABLE_CUSTOM_TAGS,
	DEFAULT_AVAILABLE_TEMPLATES,
	DEFAULT_AVAILABLE_USERS,
	fieldSet,
	spacedToCamelCase,
	useConfigContext,
	useFormFields,
	useLogoContext,
	ALLOWED_IMAGE_TYPE_STRING,
} from "shared"
import { InlineLabelSwitch } from "shared/src/components/shadcn/components/atoms/switches"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { Tooltip, TooltipTrigger, TooltipContent } from "shared/src/components/shadcn/components/ui/tooltip"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import { CheckSquare, X } from "lucide-react"
import "./AddClient.css"

export interface AddClientPayload {
	clientName: string
	clientSubdomain: string
	credits: {
		monthly: number
		annualy: number
	}
	paymentType: string
	productType: string
	limits?: {
		templates?: { limit?: number; enforced?: boolean }
		tags?: { limit?: number; enforced?: boolean }
		users?: { limit?: number; enforced?: boolean; showToUsers?: boolean }
	}
	options?: { outputs?: { [outputName: string]: CustomOutput } }
	outputData?: { [outputName: string]: { outputType: string } }
	status: string
	billingDate: string
	freeTrialEnd?: string
	ssoEnabled: boolean
}

const REGIONS: { [key: string]: string } =
	process.env.VITE_IS_DEV === "true" || process.env.VITE_IS_TEST === "true"
		? { Test: "https://t1-admin.test-hireara.ai", Dev: "http://localhost:3001" }
		: {
				UK: "https://admin.hireara.ai",
				EU: "https://admin.eu.hireara.ai",
				US: "https://admin.us.hireara.ai",
				APAC: "https://admin.apac.hireara.ai",
			}

export function AddClient() {
	const { MAX_LOGO_SIZE, evoConfig } = useConfigContext()
	const [ssoEnabled, setSSOEnabled] = useState(evoConfig?.IDENTITY_ENABLED)
	const { sendLogoToDb } = useLogoContext()
	const [regionConfirmed, setRegionConfirmed] = useState(false)
	const [selectedRegion, setSelectedRegion] = useState<string>("")
	const normalizedOrigin = window.location.origin.replace("//www.", "//")

	const [limits, setLimits] = useState<{
		templates: { limit: number; enforced: boolean }
		tags: { limit: number; enforced: boolean }
		users: { limit: number; enforced: boolean; showToUsers: boolean }
	}>({
		templates: { limit: DEFAULT_AVAILABLE_TEMPLATES, enforced: false },
		tags: { limit: DEFAULT_AVAILABLE_CUSTOM_TAGS, enforced: false },
		users: { limit: DEFAULT_AVAILABLE_USERS, enforced: false, showToUsers: false },
	})
	const [landingPage, setLandingPage] = useState<{ currentLabel: string; addOutput: boolean; label: string }>({
		currentLabel: "Landing Page",
		addOutput: false,
		label: "",
	})
	const [loading, setLoading] = useState(false)
	const [showSuccess, setShowSuccess] = useState(false)
	const history = useHistory()
	const [logo, setLogo] = useState<File | null>(null)
	const subdomainSuggestion = useCallback((clientName: string) => {
		if (clientName.length > 0) {
			const parts = clientName.split(" ")
			if (parts[parts.length - 1].length < 4) {
				parts.pop()
			}
			return parts.join("-").toLowerCase()
		} else {
			return ""
		}
	}, [])
	const [fields, handleFieldChange, updateFields] = useFormFields({
		clientName: "",
		subdomain: "",
		monthlyCredits: 0,
		paymentType: "",
		productType: "",
		status: "",
		billingDate: "",
		freeTrialEnd: "",
	})
	const validateFields = useMemo(() => {
		return (
			fields.clientName.length > 0 &&
			fields.subdomain.length > 0 &&
			fields.monthlyCredits > 0 &&
			fields.status.length > 0 &&
			fields.paymentType &&
			fields.productType &&
			fields.billingDate.length > 0 &&
			fields.status.length > 0 &&
			(fields.status !== "Free Trial" || fields.freeTrialEnd.length > 0)
		)
	}, [fields])

	useEffect(() => {
		updateFields({ subdomain: subdomainSuggestion(fields.clientName) })
	}, [fields.clientName, subdomainSuggestion]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleSuccessAnimationEnd = () => {
		setShowSuccess(false)
	}

	const handleLogoChange = (event) => {
		const file: File = event.target.files[0]
		if (file.size > MAX_LOGO_SIZE) {
			// eslint-disable-next-line no-template-curly-in-string
			alert(`Logo should be no bigger than ${bytesToMegabytes(MAX_LOGO_SIZE)}MB`)
			event.target.value = ""
			return
		}
		setLogo(file)
	}

	const handleLimitsChange = (type: string, key: string, value: boolean | number) => {
		setLimits((prevLimits) => ({
			...prevLimits,
			[type]: {
				...prevLimits[type],
				[key]: value,
			},
		}))
	}

	const onSsoEnabledChanged = (value: boolean) => {
		setSSOEnabled(value)
	}

	const handleCreate = () => {
		setLoading(true)
		const paymentTypeKey = fieldSet.paymentType.find((option) => option.label === fields.paymentType)?.key
		const productTypeKey = fieldSet.productType.find((option) => option.label === fields.productType)?.key
		const statusKey = fieldSet.status.find((option) => option.label === fields.status)?.key
		const payload: AddClientPayload = {
			clientName: fields.clientName,
			clientSubdomain: fields.subdomain,
			credits: { monthly: Number(fields.monthlyCredits), annualy: fields.monthlyCredits * 12 },
			limits: limits,
			paymentType: paymentTypeKey || "",
			productType: productTypeKey || "",
			status: statusKey,
			billingDate: fields.billingDate,
			ssoEnabled: ssoEnabled,
		}

		if (landingPage.addOutput) {
			payload.options = {
				outputs: { [landingPage.label]: { name: landingPage.label, dataType: "url" } },
			}
			payload.outputData = { [landingPage.label]: { outputType: "return" } }
		}
		if (fields.status === "Free Trial") {
			payload.freeTrialEnd = fields.freeTrialEnd
		}
		adminAddClient(payload)
			.then((newClientId) => {
				if (logo) {
					sendLogoToDb(logo, newClientId, false)
				}
				history.push(`/client/${newClientId}/login`)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	function get_enable_sso_section() {
		if (evoConfig?.IDENTITY_ENABLED) {
			return (
				<InputLikeGridWithLabel labelFor={"ssoEnabled"} label="SSO Enabled">
					<div style={{ width: "45px" }}>
						<InlineLabelSwitch checked={ssoEnabled} onCheckedChange={onSsoEnabledChanged} />
					</div>
				</InputLikeGridWithLabel>
			)
		}

		return null
	}

	return (
		<>
			<h1 className="bs-h1" style={{ marginTop: 20 }}>
				Add New Client
			</h1>
			<CardView style={{ padding: "30px", maxWidth: "600px", margin: "20px auto" }}>
				<div className="add-client">
					{regionConfirmed ? (
						<>
							<SignUpInputField
								label="Client Name"
								type="text"
								placeholder="Add client name"
								fieldName="clientName"
								initialValue={fields.clientName}
								autofocus={true}
								required={true}
								inputFieldHandler={handleFieldChange}
								labelPosition="inline"
								size="full-width"
							/>
							<SignUpInputField
								label="Subdomain:"
								type="text"
								placeholder="Add client subdomain"
								fieldName="subdomain"
								required={true}
								initialValue={fields.subdomain}
								inputFieldHandler={handleFieldChange}
								labelPosition="inline"
								size="full-width"
							/>
						</>
					) : (
						<div>
							<CustomDropdown
								actions={Object.keys(REGIONS).map((region) => ({
									label: region,
									key: region,
								}))}
								bestOption="Select a region"
								value={selectedRegion}
								onChange={(e) => {
									const region = e.target.value
									const regionUrl = REGIONS[region]
									if (regionUrl === normalizedOrigin) {
										setRegionConfirmed(true)
									} else {
										setSelectedRegion(region)
									}
								}}
							/>
							{selectedRegion && REGIONS[selectedRegion] !== normalizedOrigin && (
								<>
									<div style={{ marginTop: "10px", color: "var(--ara-red)" }}>
										You are currently in the wrong admin site for {selectedRegion}, please go to:{" "}
									</div>
									<div>
										<a
											style={{ color: "var(--primary)" }}
											href={`${REGIONS[selectedRegion]}/addclient`}
											target="_blank"
											rel="noopener noreferrer">
											{REGIONS[selectedRegion]}
										</a>
									</div>
								</>
							)}
						</div>
					)}
					{fields.clientName && fields.subdomain && (
						<>
							<hr />
							<SignUpInputField
								label="Monthly credits"
								type="number"
								placeholder="Monthly credits"
								fieldName="monthlyCredits"
								required={true}
								initialValue={fields.monthlyCredits?.toString()}
								inputFieldHandler={handleFieldChange}
								labelPosition="inline"
								size="full-width"
								step={100}
								min={0}
							/>
							<SignUpSelectField
								label="Payment type"
								placeholder="Select an option"
								fieldName="paymentType"
								required={true}
								options={fieldSet.paymentType.map((option) => option.label)}
								updateField={updateFields}
								labelPosition="inline"
								size="full-width"
							/>
							<SignUpInputField
								label="Billing Start Date"
								type="date"
								placeholder="Billing Start Date"
								fieldName="billingDate"
								required={true}
								initialValue={fields.billingDate}
								inputFieldHandler={handleFieldChange}
								labelPosition="inline"
								size="full-width"
							/>
							<SignUpSelectField
								label="Product type"
								placeholder="Select an option"
								fieldName="productType"
								required={true}
								options={fieldSet.productType.map((option) => option.label)}
								updateField={updateFields}
								labelPosition="inline"
								size="full-width"
							/>
							<SignUpSelectField
								label="Status"
								placeholder="Select an option"
								fieldName="status"
								required={true}
								options={fieldSet.status.map((option) => option.label)}
								updateField={updateFields}
								labelPosition="inline"
								size="full-width"
							/>
							{fields.status === "Free Trial" && (
								<SignUpInputField
									label="Free Trial End"
									type="date"
									placeholder="Free Trial End"
									fieldName="freeTrialEnd"
									required={true}
									initialValue={fields.freeTrialEnd}
									inputFieldHandler={handleFieldChange}
									labelPosition="inline"
									size="full-width"
								/>
							)}

							{get_enable_sso_section()}

							{validateFields && (
								<>
									<hr />
									<InputLikeGridWithLabel labelFor={"uploadLogo"} label="Logo:">
										<input type="file" onChange={handleLogoChange} accept={ALLOWED_IMAGE_TYPE_STRING} />
									</InputLikeGridWithLabel>
									<InputLikeGridWithLabel labelFor={"outputOptions"} label="Add output:">
										<div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
											<input
												name="outputOptions"
												type="text"
												placeholder="Output Name:"
												value={landingPage.currentLabel}
												onChange={(e) => setLandingPage((prev) => ({ ...prev, currentLabel: e.target.value }))}
											/>
											<Button
												style={{ margin: 0 }}
												onClick={() => {
													setLandingPage((prev) => ({
														...prev,
														addOutput: true,
														label: spacedToCamelCase(landingPage.currentLabel),
													}))
													setShowSuccess(true)
												}}>
												{!landingPage.addOutput ? "Add Digital CV" : "Change name"}
											</Button>
											{!landingPage.addOutput ? (
												""
											) : showSuccess ? (
												<CheckSquare
													color={"var(--ara-success)"}
													size={30}
													onAnimationEnd={handleSuccessAnimationEnd}
													className="success-animation"
												/>
											) : (
												<Tooltip>
													<TooltipTrigger asChild>
														<X
															className="clickable"
															size={26}
															onClick={() =>
																setLandingPage((prev) => ({
																	...prev,
																	addOutput: false,
																	currentLabel: "Landing Page",
																	label: "",
																}))
															}
														/>
													</TooltipTrigger>
													<TooltipContent>Remove output</TooltipContent>
												</Tooltip>
											)}
										</div>
									</InputLikeGridWithLabel>
									<hr />
									<div className="add-limits">
										<div style={{ paddingLeft: "5px" }}>Add Limits:</div>
										<div className="add-limits-header">
											<div>Show Limit</div>
											<div>Set Limit</div>
											<div>Enforce</div>
										</div>
										<div className="add-limits-limit">Users:</div>
										<div className="add-limits-set-limits">
											<InlineLabelSwitch
												checked={limits.users?.showToUsers ?? false}
												onCheckedChange={(value) => handleLimitsChange("users", "showToUsers", value)}
											/>
											<input
												type="text"
												inputMode="numeric"
												pattern="[0-9]*"
												style={{ width: "60px" }}
												value={limits.users?.limit ?? ""}
												onChange={(e) => handleLimitsChange("users", "limit", Number(e.target.value))}
											/>
											<InlineLabelSwitch
												checked={limits.users?.enforced ?? false}
												onCheckedChange={(value) => handleLimitsChange("users", "enforced", value)}
											/>
										</div>
										<div className="add-limits-limit">Templates:</div>
										<div className="add-limits-set-limits">
											<div></div> {/* Empty div to maintain grid structure */}
											<input
												type="text"
												inputMode="numeric"
												pattern="[0-9]*"
												style={{ width: "60px" }}
												value={limits.templates?.limit ?? ""}
												onChange={(e) => handleLimitsChange("templates", "limit", Number(e.target.value))}
											/>
											<InlineLabelSwitch
												checked={limits.templates?.enforced ?? false}
												onCheckedChange={(value) => handleLimitsChange("templates", "enforced", value)}
											/>
										</div>
										<div className="add-limits-limit">AI Prompts:</div>
										<div className="add-limits-set-limits">
											<div></div> {/* Empty div to maintain grid structure */}
											<input
												type="text"
												inputMode="numeric"
												pattern="[0-9]*"
												style={{ width: "60px" }}
												value={limits.tags?.limit ?? ""}
												onChange={(e) => handleLimitsChange("tags", "limit", Number(e.target.value))}
											/>
											<InlineLabelSwitch
												checked={limits.tags?.enforced ?? false}
												onCheckedChange={(value) => handleLimitsChange("tags", "enforced", value)}
											/>
										</div>
									</div>
								</>
							)}
						</>
					)}
				</div>
				<div style={{ display: "flex", justifyContent: "end", marginTop: "40px" }}>
					<Button loading={loading} onClick={handleCreate} disabled={!validateFields}>
						Create Client
					</Button>
				</div>
			</CardView>
		</>
	)
}
